import { FieldGroupRenderer } from '@components/forms/FieldGroupRenderer';
import { RadioCardGroup } from '@components/forms/RadioCardGroup';
import { Button } from '@components/ui/button';
import { Stepper } from '@components/ui/stepper';
import { useCaseInputStorage } from '@hooks/useCaseInputStorage';
import { useClearCaseError, useUpdateCaseInput } from '@hooks/useCases';
import { type StepConfig, useStepper } from '@hooks/useStepper';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { TrackClick } from '@/components/TrackClick';
import { KnipperCaseState } from '@/constants/caseStates';
import type { FieldGroup } from '@/types/formSchema';
import { getMirrorUpdates } from '@/utils/autoCalculate';
import { initializeFormData, separateMetadataGroup, validateGroup } from '@/utils/cases';
import { deepDiff } from '@/utils/deepDiff';
import { coerceFormDataToSchema } from '@/utils/formSchema';
import { normalizePhoneFieldsForSave } from '@/utils/phone';
import { applyRequiredFieldsToGroups, getRequiredFieldsFromGroups } from '@/utils/schemaValidation';

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

interface CaseInputFormProps {
  /** Case record ID */
  caseId: string;
  /** Call ID for localStorage key (optional) */
  callId?: string | null;
  /** Case details object */
  caseDetails: any;
  /** Pre-parsed input field groups from workflow UI schema */
  inputGroups: FieldGroup[];
  /** Custom handler for last step - called after validation and save succeed */
  lastStepNext?: {
    /** Button label to show on last step (replaces default "Save") */
    label: string;
    /** Callback after successful save on last step */
    onNext: () => void;
  };
  /** Initial step index to start at (0-based) */
  initialStep?: number;
  /** Extracted input data from AI (field ID → suggested value) */
  extractedInputData?: Record<string, any> | null;
  /** Extracted hints from AI (field ID → hint text) */
  extractedInputHints?: Record<string, string> | null;
}

/** Handle type for imperative methods exposed by CaseInputForm */
export interface CaseInputFormHandle {
  /** Validates all fields across all groups. Returns true if valid, false if errors found. */
  validateAll: () => boolean;
  /** Saves unsaved form data. Returns true if save succeeded (or no changes), false on error. */
  saveFormData: () => Promise<boolean>;
}

/* -----------------------------------------------------------------------------
 * Helper Functions
 * -------------------------------------------------------------------------- */

/**
 * Convert FieldGroups to StepConfig for the stepper
 */
function fieldGroupsToSteps(groups: FieldGroup[]): StepConfig[] {
  return groups.map((group, index) => ({
    id: `step-${index}`,
    title: group.label,
  }));
}

/* -----------------------------------------------------------------------------
 * Component
 * -------------------------------------------------------------------------- */

export const CaseInputForm = forwardRef<CaseInputFormHandle, CaseInputFormProps>(
  (
    {
      caseId,
      callId,
      caseDetails,
      inputGroups,
      lastStepNext,
      initialStep = 0,
      extractedInputData,
      extractedInputHints,
    },
    ref,
  ) => {
    const updateCaseInput = useUpdateCaseInput();
    const clearCaseError = useClearCaseError();

    // Get existing input data from case details
    const existingInputData = caseDetails?.input_data?.data || caseDetails?.input_data || null;

    // Initialize form data from existing data (useMemo prevents form resets on re-render)
    const initializedFormData = useMemo(
      () => initializeFormData(inputGroups, existingInputData),
      [inputGroups, existingInputData],
    );

    // Use localStorage hook for form data persistence
    const { formData, updateFormData, getFieldSuggestionProps, clearStorage } = useCaseInputStorage(
      {
        caseId,
        callId,
        initialInputData: initializedFormData,
        extractedInputData,
        extractedInputHints,
      },
    );

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Apply required fields based on current form data (especially case_type)
    // This recomputes whenever formData changes to handle conditional requirements
    const inputGroupsWithRequired = useMemo(() => {
      return applyRequiredFieldsToGroups(inputGroups, formData);
    }, [inputGroups, formData]);

    // Separate metadata group from other groups (using enhanced groups)
    const { metadataGroup, otherGroups } = separateMetadataGroup(inputGroupsWithRequired);

    // Convert non-metadata field groups to stepper steps
    const steps = fieldGroupsToSteps(otherGroups);

    // Initialize stepper
    const { activeStep, isLastStep, canGoBack, goNext, goBack, goToStep, getStepState } =
      useStepper({
        steps,
        initialStep,
      });

    // Get current group based on active step (from sorted non-metadata groups)
    const currentGroup = otherGroups[activeStep];

    // Handle field change (with localStorage auto-save)
    const handleFieldChange = useCallback(
      (fieldId: string, value: any) => {
        // Update form data (auto-saves to localStorage)
        updateFormData(fieldId, value);

        // Mirror fields based on schema-driven mirrorWhenField/mirrorWhenValue conditions
        // Use newFormData to avoid stale closure — reflects the just-applied field change
        const newFormData = { ...formData, [fieldId]: value };
        const mirrorUpdates = getMirrorUpdates(inputGroups, newFormData, fieldId, value);
        Object.entries(mirrorUpdates).forEach(([targetId, mirrorValue]) => {
          updateFormData(targetId, mirrorValue);
        });

        // When case_type changes, clear errors for fields that are no longer required
        if (fieldId === 'case_type') {
          const newFormData = { ...formData, [fieldId]: value };
          const newRequiredFields = getRequiredFieldsFromGroups(inputGroups, newFormData);
          setFieldErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            let hasChanges = false;

            // Remove errors for fields that are no longer required
            Object.keys(newErrors).forEach(errorFieldId => {
              if (!newRequiredFields.has(errorFieldId)) {
                delete newErrors[errorFieldId];
                hasChanges = true;
              }
            });

            return hasChanges ? newErrors : prevErrors;
          });
        } else {
          // Clear error for the specific field when user starts typing
          setFieldErrors(prevErrors => {
            if (prevErrors[fieldId]) {
              const newErrors = { ...prevErrors };
              delete newErrors[fieldId];
              return newErrors;
            }
            return prevErrors;
          });
        }
      },
      [updateFormData, formData, inputGroups],
    );

    // Handle field blur - trim string values
    const handleFieldBlur = useCallback(
      (fieldId: string) => {
        const currentValue = formData[fieldId];
        if (typeof currentValue === 'string') {
          const trimmedValue = currentValue.trim();
          if (trimmedValue !== currentValue) {
            updateFormData(fieldId, trimmedValue);
          }
        }
      },
      [formData, updateFormData],
    );

    // Save form data (used on step change and final submit)
    // Returns true if save succeeded (or nothing to save), false on error
    const saveFormData = useCallback(
      async (showSuccessToast = false): Promise<boolean> => {
        if (!caseId || !caseDetails) {
          return true;
        }

        try {
          // Compute diff between original and current form data. Coerce
          // formData to declared schema types first so the diff reflects
          // schema-correct values (numeric fields as numbers, not strings).
          // Phone fields get their `+` back first, so a number whose only change is
          // the prefix is written once and compares equal afterwards.
          const originalData = caseDetails?.input_data?.data || {};
          const coercedFormData = normalizePhoneFieldsForSave(
            inputGroups,
            coerceFormDataToSchema(formData, inputGroups),
          );
          const changedFields = deepDiff(originalData, coercedFormData);

          // If nothing changed, skip the update
          if (!changedFields) {
            if (showSuccessToast) {
              toast.success('No changes to save');
            }
            return true;
          }

          const updatedInputData = {
            schema_version: caseDetails?.input_data?.schema_version || '1.0',
            data: changedFields,
          };

          await new Promise((resolve, reject) => {
            updateCaseInput.mutate(
              { caseId, inputData: updatedInputData },
              {
                onSuccess: resolve,
                onError: reject,
              },
            );
          });

          // In review with a PQC agent validation error set (editing from
          // review): the user has addressed the flagged fields, so clear the
          // persisted error after a successful save. Non-blocking — the
          // workflow re-validates at submission anyway.
          if (caseDetails?.state === KnipperCaseState.REVIEW_NEEDED && caseDetails?.error) {
            clearCaseError.mutate({ caseId });
          }

          // Clear localStorage after successful save
          if (showSuccessToast) {
            clearStorage();
            toast.success('Case input updated successfully');
            // Analytics: success is implicit — the <TrackClick name="case:save"> click on the
            // Save button already captures the interaction; there is no UPDATE/submit event type.
          }
          return true;
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to update case input';
          toast.error(errorMessage);
          return false;
        }
      },
      [caseId, caseDetails, formData, inputGroups, updateCaseInput, clearCaseError, clearStorage],
    );

    // Expose validateAll and saveFormData methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validateAll: () => {
          // Validate all groups (metadata + all other groups)
          let allErrors: Record<string, string> = {};
          let firstErrorStepIndex: number | null = null;
          let firstErrorFieldId: string | null = null;

          // Validate metadata group (metadata errors don't affect step navigation)
          if (metadataGroup) {
            const metadataErrors = validateGroup(metadataGroup, formData);
            if (Object.keys(metadataErrors).length > 0 && firstErrorFieldId === null) {
              firstErrorFieldId = Object.keys(metadataErrors)[0];
            }
            allErrors = { ...allErrors, ...metadataErrors };
          }

          // Validate all other groups and track which step has the first error
          for (let i = 0; i < otherGroups.length; i++) {
            const group = otherGroups[i];
            const groupErrors = validateGroup(group, formData);
            if (Object.keys(groupErrors).length > 0) {
              if (firstErrorStepIndex === null) {
                firstErrorStepIndex = i;
                firstErrorFieldId = Object.keys(groupErrors)[0];
              }
              allErrors = { ...allErrors, ...groupErrors };
            }
          }

          if (Object.keys(allErrors).length > 0) {
            setFieldErrors(prev => ({ ...prev, ...allErrors }));

            // Navigate to the step with the first error
            if (firstErrorStepIndex !== null) {
              goToStep(firstErrorStepIndex);
            }

            // Focus on first error field after navigation
            if (firstErrorFieldId) {
              setTimeout(() => {
                const errorField = document.getElementById(firstErrorFieldId);
                if (errorField) {
                  errorField.focus();
                  errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
            return false;
          }

          return true;
        },
        saveFormData: () => saveFormData(),
      }),
      [metadataGroup, otherGroups, formData, goToStep, saveFormData],
    );

    // Validate current step and proceed
    const handleNext = useCallback(async () => {
      if (!currentGroup) return;

      // Validate current group
      let errors = validateGroup(currentGroup, formData);

      // Also validate metadata group on the last step
      if (isLastStep && metadataGroup) {
        const metadataErrors = validateGroup(metadataGroup, formData);
        errors = { ...errors, ...metadataErrors };
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...errors }));
        // Focus on first error field
        const firstErrorId = Object.keys(errors)[0];
        setTimeout(() => {
          const errorField = document.getElementById(firstErrorId);
          if (errorField) {
            errorField.focus();
            errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      if (isLastStep) {
        // Submit the form with success toast
        await saveFormData(true);
        // Call custom last step handler if provided
        if (lastStepNext?.onNext) {
          lastStepNext.onNext();
        }
      } else {
        // Save and go to next step
        await saveFormData();
        goNext();
      }
    }, [currentGroup, formData, isLastStep, goNext, saveFormData, lastStepNext, metadataGroup]);

    // Handle back navigation
    const handleBack = useCallback(async () => {
      // Save current step data before going back
      await saveFormData();
      goBack();
    }, [goBack, saveFormData]);

    // If no groups, show empty state
    if (inputGroups.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">No form schema available for this workflow.</p>
        </div>
      );
    }

    const isSaving = updateCaseInput.isPending;

    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex flex-col gap-4 pt-4 px-4 shrink-0 border-b pb-3">
          {/* Row 1: Title and navigation buttons */}
          <div className="flex items-center justify-between">
            <h3 className="text-muted-foreground font-medium text-sm">Case Input</h3>
            <div className="flex items-center gap-3">
              {canGoBack && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={handleBack}
                  disabled={isSaving}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Back'}
                </Button>
              )}
              <TrackClick name="case:save">
                <Button type="button" size="xs" onClick={handleNext} disabled={isSaving}>
                  {isSaving ? (
                    'Saving...'
                  ) : isLastStep ? (
                    lastStepNext?.label || 'Save'
                  ) : (
                    <>
                      Save & Proceed
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </>
                  )}
                </Button>
              </TrackClick>
            </div>
          </div>
          {/* Row 2: Stepper spans the full width so many-step flows don't crowd */}
          <Stepper
            steps={steps}
            getStepState={getStepState}
            onStepClick={goToStep}
            className="w-full"
          />
        </div>
        {/* Header with stepper and navigation */}
        <div className="flex-1 overflow-y-auto px-4 relative">
          <div className="py-4">
            {/* Metadata section (case_type) - shown at top of every step */}
            {metadataGroup && (
              <RadioCardGroup
                group={metadataGroup}
                formData={formData}
                fieldErrors={fieldErrors}
                disabled={isSaving}
                onFieldChange={handleFieldChange}
                variant="inline"
              />
            )}

            {/* Current step fields */}
            {currentGroup && (
              <FieldGroupRenderer
                group={currentGroup}
                formData={formData}
                fieldErrors={fieldErrors}
                disabled={isSaving}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
                getFieldSuggestionProps={getFieldSuggestionProps}
                caseRecordId={caseId}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

CaseInputForm.displayName = 'CaseInputForm';
